import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, Pressable, StyleSheet, Text, View } from 'react-native';

import { TextContentRender } from './components/TextRender';

import { ChartMessageItem, MessageType } from '../../constants/types';
import { MediaPreviewItem, MessageSchema } from '../../realm/schema';
import { beforeDatePlus, getAvatarByItem, getDisplayName } from '../../utils/utils';
import { VoiceContentRender } from './components/VoiceContentRender';
import { TalkContentRender } from './TalkContentRender';
import { LeftView } from './CListItem';
import BaseScreen from '../BaseScreen';
import NavBar from '../../components/NavBar';
import { color_333333, color_666666, color_999999, color_main, ColorGrey, white } from '../../assets/style/colors';
import { MainNavProps } from '../navigation/main/MainParamList';
import { Str_ChatRecord, Str_File } from '../../language/string_res';
import { useAppNavigation } from '../navigation';
import { useChatStore } from '../../store/ChatStore';
import * as ImSdk from '../../../imsdk/api';
import { ImageContentRender } from './components/ImageContentRender';
import { VideoContentRender } from './components/VideoContentRender';
import { GroupMember } from '../../../imsdk/db/t_group_member';
import { User } from '../../../imsdk/db/t_user';
import { ScreenCloseModal } from '../../components/ScreenModal';
import { DocumentRender } from './components/DocumentRender';

const ContentRender = (props: { item: MessageSchema; isSelf: boolean; auditStatus: number; isAdmin?: boolean; isTalk?: boolean }) => {
	const textStyle = props.isSelf ? chatViewStyles.sendedText : chatViewStyles.receivedText;
	switch (props.item.type) {
		case MessageType.TEXT:
			return <TextContentRender {...props} />;
		case MessageType.VOICE:
			return <VoiceContentRender {...props} styles={textStyle} />;
		case MessageType.IMAGE:
			return <ImageContentRender {...props} />;
		case MessageType.VIDEO:
			return <VideoContentRender {...props} />;
		case MessageType.TALK:
			return <TalkContentRender {...props} />;
		case MessageType.DOCUMENT:
			return <DocumentRender {...props} />;

		default:
			return props.item.content ? <Text style={textStyle}>{props.item.content}</Text> : null;
	}
};

const ChatItem = (props: { uid: number; users?: (User | GroupMember)[]; item: ChartMessageItem; isAdmin: boolean }) => {
	const navigation = useAppNavigation();
	function onClick() {
		const type = props.item.type;
		if (type !== MessageType.IMAGE && type !== MessageType.VIDEO && type !== MessageType.VOICE) {
			return;
		}

		const item: MediaPreviewItem = {
			type: type,
			mid: props.item.mid,
			index: 0,
			currentIndex: 0,
			content: props.item.content,
			upload_path: props.item.upload_path,
			thumbnailPath: props.item.thumbnailPath,
		};

		navigation.navigate('MediaPreviewScreen', { items: [item], initialIndex: 0 });
	}
	const [userName, setUserName] = useState<string>(
		props.item.user ? props.item.user.nick || props.item.user.name || props.item.user.remark || '' : ''
	);

	useEffect(() => {
		if (props.users) {
			const index = props.users.findIndex((v) => v.uid === props.item.user?.uid);
			if (index !== -1 && props.users[index].remark) {
				const n = props.users[index].nick || props.users[index].name;
				if (n) {
					setUserName(`${n}(${props.users[index].remark})`);
				} else {
					setUserName(props.users[index].remark || '');
				}
			}
		}
	}, [props.uid, props.users]);
	return (
		<View style={[chatViewStyles.msgItem, chatViewStyles.msgItemLeft]}>
			<Text style={chatViewStyles.time}>{beforeDatePlus(props.item.stime)}</Text>
			<View style={[chatViewStyles.avatarContent]}>
				<LeftView left={getAvatarByItem(props.item.user)} />
			</View>

			<View style={chatViewStyles.msgContent}>
				<Text style={[chatViewStyles.msgUserName]}>{userName}</Text>
				<View style={chatViewStyles.msgContentBox}>
					<Pressable onPress={onClick}
						style={[chatViewStyles.msgContentRadius,
						{
							backgroundColor: ColorGrey,
							shadowColor: 'rgba(0,0,0,0.1)',
							shadowOffset: { width: 0, height: 1 },
							shadowOpacity: 1,
							shadowRadius: 3,
							elevation: 2,
							paddingVertical: props.item.type === MessageType.TEXT ? 6 : 0
						}]}>
						<ContentRender auditStatus={0} isAdmin={props.isAdmin} item={props.item} isSelf={false} isTalk={true} />
					</Pressable>
				</View>
			</View>
		</View>
	);
};

export function ChatTalk({ route }: MainNavProps<'ChatTalk'>) {
	const { chatState } = useChatStore();
	const adminSet = chatState.adminSet;
	const newListRef = useRef<FlatList>(null);
	const [loading] = useState(false);
	const [users, setUsers] = useState(chatState.curTopicWithSummary?.users);
	const uid = ImSdk.getInstance().CurrentUid();
	const isAdmin = useMemo(() => {
		if (!adminSet || !adminSet.size) {
			return false;
		}
		return adminSet.has(uid);
	}, [adminSet]);
	const { tid, title, selectMessages } = route.params;
	useEffect(() => {
		if (tid !== chatState.curTopicWithSummary.topic.tid || !users || users.length === 0) {
			ImSdk.getInstance()
				.GetTopicDetail(tid, true)
				.then((r) => {
					setUsers(r.topic?.users);
				});
		}
	}, []);

	return (
		<ScreenCloseModal title={title || Str_ChatRecord}>
			<FlatList
				ref={newListRef}
				style={chatViewStyles.content}
				refreshing={loading}
				inverted={true}
				keyExtractor={(item) => `${item.mid || item.cid}-${item.stime || Date.now()}-${item.type}-${item.sender}`}
				data={selectMessages}
				contentContainerStyle={[chatViewStyles.flatList]}
				renderItem={({ item }) => <ChatItem uid={uid} users={users} item={item} isAdmin={isAdmin} />}
			/>
		</ScreenCloseModal>
	);
}

const chatViewStyles = StyleSheet.create({
	content: {
		backgroundColor: '#ffffff',
		flex: 1,
	},
	flatList: {
		flexGrow: 1,
		justifyContent: 'flex-end',
		paddingBottom: 20,
		position: 'relative',
	},
	time: {
		position: 'absolute',
		fontSize: 12,
		color: color_999999,
		right: 10,
		top: 2,
		zIndex: 10,
	},
	msgItem: {
		display: 'flex',
		marginBottom: 20,
		paddingHorizontal: 12,
	},
	msgItemLeft: {
		flexDirection: 'row',
	},
	msgItemRight: {
		flexDirection: 'row-reverse',
	},
	msgContent: {
		// width: '80%',
		position: 'relative',
		maxWidth: '80%',
		marginHorizontal: 8,
	},
	msgUserName: {
		fontSize: 12,
		color: color_999999,
		marginBottom: 4,
	},
	userNameLeft: {
		textAlign: 'left',
	},
	userNameRight: {
		textAlign: 'right',
	},
	msgContentRadius: {
		borderRadius: 4,
		fontSize: 14,
	},
	quoteContentRadius: {
		marginTop: 2,
		borderRadius: 4,
		fontSize: 12,
		overflow: 'hidden',
		backgroundColor: ColorGrey,
	},
	receivedContent: {
		backgroundColor: ColorGrey,
		alignSelf: 'flex-start',
	},
	sendedContent: {
		backgroundColor: color_main,
		alignSelf: 'flex-end',
	},
	quoteText: {
		paddingHorizontal: 12,
		paddingVertical: 4,
		color: color_666666,
	},
	receivedText: {
		color: color_333333,
		paddingHorizontal: 12,
		paddingVertical: 10,
	},
	sendedText: {
		color: white,
		paddingHorizontal: 12,
		paddingVertical: 10,
	},
	systemRender: {
		display: 'flex',
		paddingHorizontal: 12,
		alignSelf: 'center',
	},
	systemRenderWrap: {
		backgroundColor: ColorGrey,
		paddingHorizontal: 12,
		paddingVertical: 6,
		borderRadius: 12,
		overflow: 'hidden',
		marginBottom: 16,
		display: 'flex',
	},
	systemRenderText: {
		fontSize: 12,
		color: color_333333,
	},
	avatarContent: {
		position: 'relative',
	},
	readStatus: {
		position: 'absolute',
		top: 42,
	},
	msgContentBox: {
		position: 'relative',
		display: 'flex',
	},
	auditStatus: {
		position: 'absolute',
		top: 0,
	},
	auditStatusRight: {
		left: -28,
	},
	auditStatusLeft: {
		right: -28,
	},
});
